/**
 * useSubtitlesState Hook
 *
 * Manages all subtitle-only state using useReducer for predictable state
 * transitions.  Mirrors the pattern used in useDubbingState.
 */

import { useReducer, useCallback, useMemo } from 'react';
import type {
  SubtitlesState,
  SubtitlesAction,
  SubtitleOutputMode,
  SubtitleFormat,
  SubtitleJobStatus,
  SubtitleStatus,
  SubtitleStyle,
} from '../types';
import { DEFAULT_SUBTITLE_STYLE } from '../types';
import type { MediaType } from '@/components/common/MediaUploader';
import { getAllNLLBLanguages } from '@/utils/languageMapping';
import { resolveInitialLanguages, persistLanguages } from '@/utils/browserLanguage';

// Build supported-language set once at module level
const NLLB_CODES: ReadonlySet<string> = new Set(getAllNLLBLanguages().map((l) => l.code));

// ============================================================================
// Initial state factory
// =============================================================================

/**
 * Build the initial state with browser-language detection for targetLanguages.
 * Called once per hook mount (useReducer third-arg initializer).
 */
function buildInitialState(): SubtitlesState {
  return {
    // Media
    mediaFile: null,
    mediaType: null,
    mediaUrl: null,

    // Languages — source defaults to "auto"; targets default to browser lang
    sourceLanguage: 'auto',
    targetLanguages: resolveInitialLanguages('subtitle', NLLB_CODES),

    // Config — sidecar always, all 3 formats, LLM on, karaoke off
    outputMode: 'sidecar',
    formats: ['srt', 'vtt', 'ass'],
    useLlmRefinement: true,
    useKaraoke: false,

    // Hard burn
    style: DEFAULT_SUBTITLE_STYLE,
    hardBurnConfirmed: false,

    // Progress / results
    uploadProgress: 0,
    jobId: null,
    jobStatus: null,
    jobProgress: 0,
    jobMessage: '',
    outputFilePath: null,

    // Status
    status: 'idle',
    error: null,
  };
}

// Static fallback used by RESET / CLEAR_MEDIA reducer branches
const initialState: SubtitlesState = {
  mediaFile: null,
  mediaType: null,
  mediaUrl: null,
  sourceLanguage: 'auto',
  targetLanguages: [],
  outputMode: 'sidecar',
  formats: ['srt', 'vtt', 'ass'],
  useLlmRefinement: true,
  useKaraoke: false,
  style: DEFAULT_SUBTITLE_STYLE,
  hardBurnConfirmed: false,
  uploadProgress: 0,
  jobId: null,
  jobStatus: null,
  jobProgress: 0,
  jobMessage: '',
  outputFilePath: null,
  status: 'idle',
  error: null,
};

// ============================================================================
// Reducer
// ============================================================================

function subtitlesReducer(state: SubtitlesState, action: SubtitlesAction): SubtitlesState {
  switch (action.type) {
    case 'SET_MEDIA':
      return {
        ...state,
        mediaFile: action.file,
        mediaType: action.mediaType,
        mediaUrl: null,
        jobId: null,
        jobStatus: null,
        jobProgress: 0,
        jobMessage: '',
        outputFilePath: null,
        error: null,
        status: 'idle',
        uploadProgress: 0,
      };

    case 'CLEAR_MEDIA':
      return {
        ...initialState,
      };

    case 'SET_MEDIA_URL':
      return {
        ...state,
        mediaUrl: action.url,
        // Backend detected type takes precedence when set
        mediaType: action.mediaType,
      };

    case 'SET_SOURCE_LANGUAGE':
      return { ...state, sourceLanguage: action.language };

    case 'SET_TARGET_LANGUAGES':
      return { ...state, targetLanguages: action.languages };

    case 'SET_OUTPUT_MODE': {
      const next = { ...state, outputMode: action.mode };
      // When switching to hard_burn: enforce single language, force ASS format, reset confirmation
      if (action.mode === 'hard_burn') {
        next.formats = ['ass'];
        next.hardBurnConfirmed = false;
        if (state.targetLanguages.length > 1) {
          next.targetLanguages = state.targetLanguages.slice(0, 1);
        }
      }
      // When switching away from hard_burn: restore all formats
      if (state.outputMode === 'hard_burn' && action.mode !== 'hard_burn') {
        next.formats = ['srt', 'vtt', 'ass'];
        next.hardBurnConfirmed = false;
      }
      return next;
    }

    case 'SET_FORMATS':
      return { ...state, formats: action.formats };

    case 'SET_LLM_REFINEMENT':
      return { ...state, useLlmRefinement: action.enabled };

    case 'SET_KARAOKE':
      return { ...state, useKaraoke: action.enabled };

    case 'SET_STYLE':
      return { ...state, style: action.style };

    case 'SET_HARD_BURN_CONFIRMED':
      return { ...state, hardBurnConfirmed: action.confirmed };

    case 'SET_UPLOAD_PROGRESS':
      return { ...state, uploadProgress: action.progress };

    case 'START_UPLOADING':
      return { ...state, status: 'uploading', uploadProgress: 0, error: null };

    case 'START_GENERATING':
      return {
        ...state,
        status: 'polling',
        jobId: action.jobId,
        jobStatus: 'pending',
        jobProgress: 0,
        jobMessage: '',
        outputFilePath: null,
        error: null,
      };

    case 'SET_JOB_STATUS':
      return {
        ...state,
        jobStatus: action.status,
        jobProgress: action.progress,
        jobMessage: action.message,
        status: (['pending', 'processing'] as SubtitleJobStatus[]).includes(action.status)
          ? 'polling'
          : state.status,
      };

    case 'SET_COMPLETED':
      return {
        ...state,
        status: 'completed',
        jobStatus: 'completed',
        jobProgress: 100,
        outputFilePath: action.outputFilePath,
      };

    case 'SET_ERROR':
      return { ...state, status: 'error', error: action.error };

    case 'CANCEL':
      // Internal UI state is 'cancelled'; jobStatus stays as last backend value (no cancelled status on server)
      return { ...state, status: 'cancelled' };

    case 'RESET':
      return { ...initialState };

    default:
      return state;
  }
}

// ============================================================================
// Hook
// ============================================================================

export interface SubtitlesStateReturn {
  state: SubtitlesState;
  actions: {
    setMedia: (file: File, mediaType: MediaType) => void;
    clearMedia: () => void;
    setMediaUrl: (url: string, mediaType: 'audio' | 'video') => void;
    setSourceLanguage: (language: string) => void;
    setTargetLanguages: (languages: string[]) => void;
    setOutputMode: (mode: SubtitleOutputMode) => void;
    setFormats: (formats: SubtitleFormat[]) => void;
    setLlmRefinement: (enabled: boolean) => void;
    setKaraoke: (enabled: boolean) => void;
    setStyle: (style: SubtitleStyle) => void;
    setHardBurnConfirmed: (confirmed: boolean) => void;
    setUploadProgress: (progress: number) => void;
    startUploading: () => void;
    startGenerating: (jobId: string) => void;
    setJobStatus: (status: SubtitleJobStatus, progress: number, message: string) => void;
    setCompleted: (outputFilePath?: string | null) => void;
    setError: (error: string) => void;
    cancel: () => void;
    reset: () => void;
  };
  // Derived flags
  isUploading: boolean;
  isGenerating: boolean;
  isCompleted: boolean;
  showForm: boolean;
}

export function useSubtitlesState(): SubtitlesStateReturn {
  // Lazy initial state: reads localStorage + navigator.language once at mount
  const [state, dispatch] = useReducer(subtitlesReducer, null, () => buildInitialState());

  // Action creators
  const setMedia = useCallback((file: File, mediaType: MediaType) => {
    dispatch({ type: 'SET_MEDIA', file, mediaType });
  }, []);

  const clearMedia = useCallback(() => {
    dispatch({ type: 'CLEAR_MEDIA' });
  }, []);

  const setMediaUrl = useCallback((url: string, mediaType: 'audio' | 'video') => {
    dispatch({ type: 'SET_MEDIA_URL', url, mediaType });
  }, []);

  const setSourceLanguage = useCallback((language: string) => {
    dispatch({ type: 'SET_SOURCE_LANGUAGE', language });
  }, []);

  const setTargetLanguages = useCallback((languages: string[]) => {
    dispatch({ type: 'SET_TARGET_LANGUAGES', languages });
    persistLanguages('subtitle', languages);
  }, []);

  const setOutputMode = useCallback((mode: SubtitleOutputMode) => {
    dispatch({ type: 'SET_OUTPUT_MODE', mode });
  }, []);

  const setFormats = useCallback((formats: SubtitleFormat[]) => {
    dispatch({ type: 'SET_FORMATS', formats });
  }, []);

  const setLlmRefinement = useCallback((enabled: boolean) => {
    dispatch({ type: 'SET_LLM_REFINEMENT', enabled });
  }, []);

  const setKaraoke = useCallback((enabled: boolean) => {
    dispatch({ type: 'SET_KARAOKE', enabled });
  }, []);

  const setStyle = useCallback((style: SubtitleStyle) => {
    dispatch({ type: 'SET_STYLE', style });
  }, []);

  const setHardBurnConfirmed = useCallback((confirmed: boolean) => {
    dispatch({ type: 'SET_HARD_BURN_CONFIRMED', confirmed });
  }, []);

  const setUploadProgress = useCallback((progress: number) => {
    dispatch({ type: 'SET_UPLOAD_PROGRESS', progress });
  }, []);

  const startUploading = useCallback(() => {
    dispatch({ type: 'START_UPLOADING' });
  }, []);

  const startGenerating = useCallback((jobId: string) => {
    dispatch({ type: 'START_GENERATING', jobId });
  }, []);

  const setJobStatus = useCallback(
    (status: SubtitleJobStatus, progress: number, message: string) => {
      dispatch({ type: 'SET_JOB_STATUS', status, progress, message });
    },
    []
  );

  const setCompleted = useCallback((outputFilePath: string | null = null) => {
    dispatch({ type: 'SET_COMPLETED', outputFilePath });
  }, []);

  const setError = useCallback((error: string) => {
    dispatch({ type: 'SET_ERROR', error });
  }, []);

  const cancel = useCallback(() => {
    dispatch({ type: 'CANCEL' });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  const actions = useMemo(
    () => ({
      setMedia,
      clearMedia,
      setMediaUrl,
      setSourceLanguage,
      setTargetLanguages,
      setOutputMode,
      setFormats,
      setLlmRefinement,
      setKaraoke,
      setStyle,
      setHardBurnConfirmed,
      setUploadProgress,
      startUploading,
      startGenerating,
      setJobStatus,
      setCompleted,
      setError,
      cancel,
      reset,
    }),
    [
      setMedia,
      clearMedia,
      setMediaUrl,
      setSourceLanguage,
      setTargetLanguages,
      setOutputMode,
      setFormats,
      setLlmRefinement,
      setKaraoke,
      setStyle,
      setHardBurnConfirmed,
      setUploadProgress,
      startUploading,
      startGenerating,
      setJobStatus,
      setCompleted,
      setError,
      cancel,
      reset,
    ]
  );

  // Derived flags
  const isUploading = state.status === 'uploading';
  const isGenerating = state.status === 'polling' || state.status === 'generating';
  const isCompleted = state.status === 'completed';
  const showForm = !isGenerating && !isCompleted;

  return { state, actions, isUploading, isGenerating, isCompleted, showForm };
}

// Derive the "status" type the way the rest of the codebase does it
export type { SubtitleStatus };
